import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * Lightweight right-side sheet with Esc-to-close, backdrop click, and slide animation.
 * Portal-rendered so CSS transforms on parents can't break fixed positioning.
 */
const SideSheet = ({ open, onClose, title, subtitle, children, width = 480, testid }) => {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [open, onClose]);

    if (!open) return null;

    return createPortal(
        <div className="fixed inset-0 z-[60] flex" data-testid={testid}>
            <div
                className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-150"
                onClick={onClose}
            />
            <div
                className="relative ml-auto h-full bg-white shadow-2xl border-l border-slate-200 flex flex-col animate-in slide-in-from-right duration-200"
                style={{ width: `min(${width}px, 92vw)` }}
            >
                <div className="flex items-start justify-between px-6 h-16 border-b border-slate-200 shrink-0">
                    <div className="pt-3 min-w-0">
                        <h3 className="font-heading font-semibold text-slate-900 truncate">{title}</h3>
                        {subtitle && <p className="text-xs text-slate-500 truncate">{subtitle}</p>}
                    </div>
                    <button
                        onClick={onClose}
                        className="h-9 w-9 mt-3 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600"
                        aria-label="Close"
                        data-testid="side-sheet-close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto scrollbar-thin">
                    {children}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SideSheet;
